import {
    AnswerEntityId,
    daysAgo,
    fullSingleLineAddress,
    IContact,
    LayoutIcon,
    ReferralMappings,
} from "@sp-crm/core";
import { AddressDisplay } from "components/contact-display/address-display";
import { EmailDisplay } from "components/contact-display/email-display";
import { FaxDisplay } from "components/contact-display/fax-display";
import { PhoneDisplay } from "components/contact-display/phone-display";
import { WebsiteDisplay } from "components/contact-display/website-display";
import { Icon } from "components/icon";
import { AdvancedSearchEntityType } from "generated/graphql";
import * as React from "react";
import { Link } from "react-router-dom";
import { friendlyDisplayName } from "../../../util/text";

interface ContactCardProps {
    contact: IContact | null;
    parentEntityType: AdvancedSearchEntityType;
    parentId: AnswerEntityId;
    href?: string;
    icon?: LayoutIcon | JSX.Element | null;
    actions?: React.ReactNode;
    children?: React.ReactNode;
}

export const ContactCardActions: React.FC<{ children: React.ReactNode }> = props => (
    <div className="flex flex-shrink-0 items-center">{props.children}</div>
);

const ContactCardName: React.FC<ContactCardProps> = props => {
    const { contact, href, icon, actions } = props;

    return (
        <div className="text-base flex items-center gap-2">
            {typeof icon === "string" ? (
                <div className="w-4">
                    <Icon name={icon} />
                </div>
            ) : (
                icon
            )}
            <div className="flex-1 min-w-0 break-words">
                {href ? (
                    <Link to={href}>{friendlyDisplayName(contact.name)}</Link>
                ) : (
                    friendlyDisplayName(contact.name)
                )}
            </div>
            {actions ? <ContactCardActions>{actions}</ContactCardActions> : null}
        </div>
    );
};

const ContactCardType: React.FC<{ contact: IContact }> = props =>
    props.contact.contactTypeMaybe
        .map(ct => ReferralMappings[ct])
        .map(mapping => (
            <div key={mapping.category} className="font-medium mb-1">
                {mapping.category} &raquo; {mapping.humanReadable}
            </div>
        ))
        .getOrElse(null);

const ContactCardWebsite: React.FC<{ contact: IContact }> = props => {
    const { contact } = props;

    if (!contact.website) {
        return null;
    }

    return <WebsiteDisplay value={contact.website} label={null} />;
};

const ContactCardNotes: React.FC<{ contact: IContact }> = props => {
    if (!props.contact.contactNotes) {
        return null;
    }

    return <p className="italic mt-2 break-words">{props.contact.contactNotes}</p>;
};

const ContactCardDateAdded: React.FC<{ contact: IContact }> = props => {
    const { contact } = props;

    if (!contact.dateAdded) {
        return null;
    }

    const date = new Date(contact.dateAdded);

    return <div className="text-xs text-gray-500 mt-2">Created {daysAgo(date, "")}</div>;
};

export const ContactCard: React.FC<ContactCardProps> = props => {
    const { contact, parentEntityType, parentId, children } = props;

    if (!contact) {
        return null;
    }

    const address = fullSingleLineAddress(contact);

    return (
        <div className="text-sm">
            <ContactCardName {...props} />
            {contact.role ? <div className="font-medium mb-1">{contact.role}</div> : null}
            {contact.relationship ? (
                <div className="font-medium mb-1">{contact.relationship}</div>
            ) : null}
            <ContactCardType contact={contact} />
            {address ? (
                <div className="mb-1">
                    <AddressDisplay value={address} />
                </div>
            ) : null}
            {contact.cellPhone ? (
                <PhoneDisplay
                    value={contact.cellPhone}
                    label="cell"
                    entityId={parentId}
                    entityType={parentEntityType}
                />
            ) : null}
            {contact.phone1 ? (
                <PhoneDisplay
                    value={contact.phone1}
                    label="other"
                    entityId={parentId}
                    entityType={parentEntityType}
                />
            ) : null}
            {contact.fax ? <FaxDisplay value={contact.fax} /> : null}
            {contact.email1 ? (
                <EmailDisplay
                    value={contact.email1}
                    label={null}
                    optOut={contact.email1OptOut}
                    entityId={parentId}
                    entityType={parentEntityType}
                />
            ) : null}
            <ContactCardWebsite contact={contact} />
            <ContactCardNotes contact={contact} />
            {children}
            <ContactCardDateAdded contact={contact} />
        </div>
    );
};
